<script setup lang="ts">
  import SettingsPage from "@/views/admin/settings/settings-page.vue";
  import { useFetchPartners, useUpdatePartner } from "@/data/hooks/partners";
  import { TextInput, MultiSelect } from "@/ui";
  import SectionTitle from "./components/section-title.vue";
  import { useForm } from "vee-validate";
  import { array, number, object, string } from "yup";
  import { toTypedSchema } from "@vee-validate/yup";
  import { useFetchOffices } from "@/data/hooks/offices";
  import { computed, watch } from "vue";
  import Button from "primevue/button";
  import { useI18n } from "vue-i18n";
  import { useToast } from "vue-toast-notification";
  import { isAcegPartner } from "@/utils/partners/isVgePartner";
  import _ from "lodash";
  import { useRouter } from "vue-router";
  import store from "@/store";
  import { PartnerType } from "@/data/actions-query";

  const { t } = useI18n();
  const toast = useToast();
  const router = useRouter();

  const { group_id } = store.getters["auth/me"];
  const { data: offices } = useFetchOffices(group_id);

  const partnerTypes = Object.values(PartnerType).map((type) => ({
    label: t(`views.partner.${type}_title`),
    value: type,
  }));

  const officeOptions = computed(() => {
    return (offices?.value ?? []).map((office) => ({
      label: office.name,
      value: office.id,
    }));
  });

  const { handleSubmit, resetForm, isSubmitting } = useForm({
    validationSchema: toTypedSchema(
      object({
        officeIds: array().of(number().required()).required(),
        types: array().of(string()), // disabled field
      }),
    ),
  });

  // Edit mode
  const { data: partners, isLoading } = useFetchPartners({ group_id });
  watch(
    partners,
    () => {
      if (partners.value) {
        const acegPartners = partners.value.items.filter(isAcegPartner);
        const types = _.uniq(acegPartners.map((partner) => partner.type));
        const officeIds = _.uniq(acegPartners.flatMap((partner) => partner.offices?.map((office) => office.id) ?? []));
        resetForm({
          values: {
            officeIds,
            types,
          },
        });
      }
    },
    { immediate: true },
  );

  const { mutateAsync: updatePartner } = useUpdatePartner();

  const onSubmit = handleSubmit(async (values) => {
    try {
      const promises = [];
      for (const partner of (partners.value?.items ?? []).filter(isAcegPartner)) {
        promises.push(
          updatePartner({
            id: partner.id,
            payload: {
              office_ids: values.officeIds,
            },
          }),
        );
      }
      await Promise.all(promises);
      toast.success("Partner succesvol aangepast");
      router.push({ name: "admin-partners" });
    } catch (error) {
      toast.error(t("errors.generic.error"));
      console.error(error);
    }
  });
</script>

<template>
  <div class="flex flex-col h-full justify-between">
    <SettingsPage title="Partner aanpassen" :is-loading="isLoading" isWithBackButton>
      <section-title title="Bedrijfsgegevens" />
      <div class="flex flex-row gap-2 lg:w-3/4">
        <TextInput class="flex-1" label="Bedrijfsnaam" modelValue="Aceg" disabled required />
        <MultiSelect class="lg:w-3/4" name="types" label="Diensten" :options="partnerTypes" optionValue="value" optionLabel="label" required disabled />
      </div>
      <MultiSelect class="mt-2 lg:w-3/4" name="officeIds" label="Kantoren" :options="officeOptions" optionValue="value" optionLabel="label" required />
    </SettingsPage>
    <div class="flex flex-row gap-2 sticky md:bottom-0 bottom-[94px] bg-white px-6 py-2 border border-gray-300 z-10">
      <Button label="Opslaan" :loading="isSubmitting" :disabled="isSubmitting" @click="onSubmit" />
      <Button label="Annuleer" outlined @click="router.push({ name: 'admin-partners' })" />
    </div>
  </div>
</template>

<style scoped>
  :deep(.p-multiselect) {
    min-width: 17rem;
  }
  :deep(.p-multiselect-label) {
    display: flex;
    flex-wrap: wrap;
  }
  :deep(.p-multiselect-token) {
    margin: 0.1rem;
  }
</style>
